import { inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { featureGuard } from '@zag/features/ui';
import { LayoutComponent } from './layout.component';
import { MinimalLayoutComponent } from './minimal-layout.component';
import {
  authGuard,
  mustChangePasswordGuard,
  provideAuthRoutes,
  requirePermission,
  requireAnyPermission,
  deniedPageGuard,
  AcceptInvitationComponent,
  ForgotPasswordComponent,
  ResetPasswordComponent,
  ForceChangePasswordComponent,
  PermissionService,
} from '@zag/auth/ui';
import { tenantReadyGuard, provisioningPageGuard, maintenancePageGuard, deprovisionedPageGuard } from './provisioning.guard';
import { FIELD_PERMISSION_CHECKER } from '@zag/ui';
// AbsenceTypeEditComponent, BalanceTypeEditComponent, AbsenceTypesCatalogComponent and
// BalanceTypesCatalogComponent are loaded lazily via loadComponent to keep @zag/timetracking/ui
// out of the initial bundle.

export const appRoutes: Route[] = [
  // Root path - smart redirect to appropriate landing page
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/dashboard',
  },
  // Accept invitation — public route, no authentication required.
  // MUST be placed before any guarded route that matches 'auth/*'.
  {
    path: 'auth/accept-invitation',
    component: AcceptInvitationComponent,
    title: 'Accept Invitation - SmartCapacity',
  },
  // Forgot password — public route, no authentication required.
  {
    path: 'auth/forgot-password',
    component: ForgotPasswordComponent,
    title: 'Forgot Password - SmartCapacity',
  },
  // Reset password (from emailed link) — public route, no authentication required.
  {
    path: 'auth/reset-password',
    component: ResetPasswordComponent,
    title: 'Reset Password - SmartCapacity',
  },
  // Provisioning progress page — redirects to /login when tenant is already ready
  {
    path: 'provisioning',
    canActivate: [provisioningPageGuard],
    loadComponent: () => import('./routes/provisioning.component').then(m => m.ProvisioningComponent),
    title: 'Setting up your workspace - SmartCapacity',
  },
  // Maintenance page — shown during rolling deploy updates; redirects to /login when ready
  {
    path: 'maintenance',
    canActivate: [maintenancePageGuard],
    loadComponent: () => import('./routes/maintenance.component').then(m => m.MaintenanceComponent),
    title: 'Update in progress - SmartCapacity',
  },
  // Deprovisioned page — shown when the tenant has been torn down
  {
    path: 'deprovisioned',
    canActivate: [deprovisionedPageGuard],
    loadComponent: () => import('./routes/deprovisioned.component').then(m => m.DeprovisionedComponent),
    title: 'Workspace unavailable - SmartCapacity',
  },
  // Auth routes (login, register) without layout — require tenant to be provisioned
  {
    path: '',
    canActivate: [tenantReadyGuard],
    children: [
      ...provideAuthRoutes({
        loginTitle: 'Login - SmartCapacity',
        registerTitle: 'Register - SmartCapacity',
      }),
    ],
  },
  // Auth access pages without main layout (pending, error)
  {
    path: 'auth',
    component: MinimalLayoutComponent,
    canActivate: [authGuard, tenantReadyGuard],
    children: [
      {
        path: 'pending',
        loadComponent: () => import('./routes/pending-access.component').then(m => m.PendingAccessComponent),
        title: 'Access Pending - SmartCapacity',
      },
      {
        path: 'error',
        loadComponent: () => import('./routes/permission-error.component').then(m => m.PermissionErrorComponent),
        title: 'Permission Error - SmartCapacity',
      },
      // Force-change-password — shown when mustChangePassword = true.
      // Must NOT carry mustChangePasswordGuard itself (would cause infinite redirect).
      {
        path: 'change-password',
        component: ForceChangePasswordComponent,
        title: 'Change Password - SmartCapacity',
      },
    ],
  },
  // Authenticated routes with full layout (header/sidebar)
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard, tenantReadyGuard, mustChangePasswordGuard],
    canActivateChild: [authGuard],
    children: [
      // Access denied page - rendered with full layout
      // Uses deniedPageGuard to redirect to /auth/pending or /auth/error if state changed
      {
        path: 'auth/denied',
        canActivate: [deniedPageGuard],
        loadComponent: () => import('./routes/denied-access.component').then(m => m.DeniedAccessComponent),
        title: 'Access Denied - SmartCapacity',
      },
      {
        path: 'profile',
        canActivate: [requirePermission('users:profile:read')],
        loadComponent: () => import('./routes/profile.component').then(m => m.ProfileComponent),
        title: 'My Profile - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.profile', icon: 'person' },
        },
      },
      {
        path: 'dashboard',
        canActivate: [requirePermission('dashboard:read')],
        loadComponent: () => import('./routes/dashboard.component').then(m => m.DashboardComponent),
        title: 'Dashboard - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.dashboard', icon: 'dashboard' },
        },
      },
      // Central approvals (story 34-1, F14): top-level entry — renders the
      // review queue (today's only feeder); further modules dock on later.
      {
        path: 'approvals',
        canActivate: [requirePermission('timetracking:review-queue:read'), featureGuard],
        loadComponent: () => import('./routes/review-queue.component').then(m => m.ReviewQueueComponent),
        title: 'Approvals - SmartCapacity',
        data: { feature: 'timetracking', breadcrumb: { label: 'navigation.approvals', icon: 'fact_check' } },
      },
      {
        path: 'my-plan',
        canActivate: [requirePermission('schedules:read:own'), featureGuard],
        data: {
          feature: 'planning',
          breadcrumb: { label: 'navigation.myPlan', icon: 'calendar_view_week' },
        },
        loadChildren: () => import('./routes/my-plan/my-plan.routes').then(m => m.MY_PLAN_ROUTES),
      },
      {
        path: 'documents',
        canActivate: [requireAnyPermission('documents:read:own', 'documents:read', 'files:read'), featureGuard],
        // The hub injects FIELD_PERMISSION_CHECKER to gate the mode switcher;
        // provide it here so the lazy-loaded component resolves the token.
        providers: [{ provide: FIELD_PERMISSION_CHECKER, useExisting: PermissionService }],
        data: { feature: 'documents', breadcrumb: { label: 'documents.hub.title', icon: 'folder_shared' } },
        loadComponent: () => import('@zag/documents/ui').then(m => m.DocumentsHubComponent),
        title: 'Documents - SmartCapacity',
      },
      // Legacy route redirects — keep bookmarks and global-search results
      // working. Use the function form so query params are emitted via a
      // UrlTree (string `redirectTo` does not preserve the query string).
      {
        path: 'my-documents',
        pathMatch: 'full',
        redirectTo: () => inject(Router).createUrlTree(['/documents'], { queryParams: { mode: 'my' } }),
      },
      {
        path: 'documents/personal',
        pathMatch: 'full',
        redirectTo: () => inject(Router).createUrlTree(['/documents'], { queryParams: { mode: 'personal' } }),
      },
      {
        path: 'planning',
        canActivate: [requirePermission('plans:read'), featureGuard],
        data: {
          feature: 'planning',
          breadcrumb: {
            label: 'navigation.planning',
            icon: 'calendar_month',
            inactive: true,
          },
        },
        loadChildren: () => import('./routes/planning/planning.routes').then(m => m.PLANNING_ROUTES),
      },
      {
        path: 'persons',
        canActivate: [requirePermission('employees:read')],
        data: {
          breadcrumb: { label: 'navigation.persons', icon: 'people' },
        },
        loadChildren: () => import('./routes/persons.routes').then(m => m.PERSONS_ROUTES),
      },
      {
        path: 'shift-applications/request/:mode',
        canActivate: [requirePermission('shift-swaps:request')],
        loadComponent: () => import('./routes/shift-swap-request-page.component').then(m => m.ShiftSwapRequestPageComponent),
        title: 'Shift Offer - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.openShifts', icon: 'event_available', parentPath: '/shift-applications' },
        },
      },
      {
        path: 'shift-swaps/workflow/:mode',
        canActivate: [requirePermission('shift-swaps:request')],
        loadComponent: () => import('./routes/shift-swap-request-page.component').then(m => m.ShiftSwapRequestPageComponent),
        title: 'Shift Workflow - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.openShifts', icon: 'event_available', parentPath: '/shift-applications' },
        },
      },
      {
        path: 'shift-applications',
        canActivate: [requirePermission('shift-applications:read')],
        loadComponent: () => import('./routes/shift-applications.component').then(m => m.ShiftApplicationsComponent),
        title: 'Shift Applications - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.openShifts', icon: 'event_available' },
        },
      },
      {
        path: 'shift-requests',
        canActivate: [requireAnyPermission('shift-swaps:read', 'requests:read')],
        loadComponent: () => import('./routes/shift-requests.component').then(m => m.ShiftRequestsComponent),
        title: 'Requests - SmartCapacity',
        data: {
          breadcrumb: { label: 'navigation.requests', icon: 'swap_horiz' },
        },
      },
      {
        path: 'reports',
        canActivate: [requireAnyPermission('reports:generate', 'plans:read'), featureGuard],
        loadComponent: () => import('./routes/reports.component').then(m => m.ReportsComponent),
        title: 'Reports - SmartCapacity',
        data: {
          feature: 'reports.view',
          breadcrumb: { label: 'navigation.reports', icon: 'bar_chart' },
        },
      },
      {
        path: 'shift-definitions',
        canActivate: [requirePermission('shifts:read'), featureGuard],
        data: {
          feature: 'planning',
          breadcrumb: {
            label: 'navigation.shiftDefinitions',
            icon: 'schedule',
          },
        },
        loadChildren: () => import('./routes/shift-definitions.routes').then(m => m.SHIFT_DEFINITIONS_ROUTES),
      },
      {
        path: 'time-tracking',
        canActivate: [
          requireAnyPermission(
            'settings:read',
            'settings:update',
            'timetracking:manual-bookings:read',
            'timetracking:my-week:read',
            'timetracking:review-queue:read',
            'timetracking:overview:read',
            'employees:balances:read',
            'timetracking:month-close:read',
            'timetracking:month-close:update',
            // F20 org-chart-position-editor: without these, a user who holds
            // ONLY organisation:* permissions is blocked by this parent guard
            // before ever reaching the child route's own permission check.
            'organisation:read',
            'organisation:update',
            'organisation:assignments:update',
            'organisation:role-assignments:update'
          ),
          featureGuard,
        ],
        data: {
          feature: 'timetracking',
          breadcrumb: { label: 'navigation.sectionTimeTracking', icon: 'schedule' },
        },
        children: [
          {
            path: 'my-bookings',
            canActivate: [requireAnyPermission('timetracking:my-week:read', 'timetracking:manual-bookings:read')],
            children: [
              {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./routes/my-bookings.component').then(m => m.MyBookingsComponent),
                title: 'My Week - SmartCapacity',
                data: { breadcrumb: { label: 'navigation.myBookings', icon: 'timer' } },
              },
              {
                path: 'capture',
                loadComponent: () => import('./routes/my-bookings.component').then(m => m.MyBookingsComponent),
                title: 'My Week - SmartCapacity',
                data: { breadcrumb: { label: 'navigation.myBookingsCapture', icon: 'keyboard' }, captureMode: true },
              },
            ],
          },
          {
            path: 'overview',
            canActivate: [requirePermission('timetracking:overview:read')],
            loadComponent: () => import('./routes/overview.component').then(m => m.OverviewComponent),
            title: 'Overview - SmartCapacity',
            data: { breadcrumb: { label: 'navigation.overview', icon: 'groups' } },
          },
          // Legacy route (pre-34-1) — the queue moved to the central /approvals entry.
          {
            path: 'review-queue',
            pathMatch: 'full',
            redirectTo: '/approvals',
          },
          // Configuration pages moved to Settings → Time Tracking (spec §3f.35).
          // Legacy deep links redirect to the new locations.
          { path: 'rules', pathMatch: 'full', redirectTo: '/settings/rules' },
          { path: 'rules/setup', pathMatch: 'full', redirectTo: '/settings/rules/setup' },
          { path: 'rules/assistant', pathMatch: 'full', redirectTo: '/settings/rules/assistant' },
          { path: 'rules/:kindKey/adjust', pathMatch: 'full', redirectTo: '/settings/rules/:kindKey/adjust' },
          { path: 'absences', pathMatch: 'full', redirectTo: '/settings/absences' },
          { path: 'absences/new', pathMatch: 'full', redirectTo: '/settings/absences/new' },
          { path: 'absences/:id', pathMatch: 'full', redirectTo: '/settings/absences/:id' },
          { path: 'balances', pathMatch: 'full', redirectTo: '/settings/balances' },
          { path: 'balances/new', pathMatch: 'full', redirectTo: '/settings/balances/new' },
          { path: 'balances/:id', pathMatch: 'full', redirectTo: '/settings/balances/:id' },
          // Organigramm moved to Settings (F20, PO decision 2026-07-08 — admin
          // activities live under Settings). Legacy deep links redirect.
          { path: 'organigramm', pathMatch: 'full', redirectTo: '/settings/organigramm' },
          { path: 'organigramm/live', pathMatch: 'full', redirectTo: '/settings/organigramm' },
          {
            path: 'balance-overview',
            canActivate: [requirePermission('employees:balances:read')],
            loadChildren: () => import('./routes/balance-overview.routes').then(m => m.BALANCE_OVERVIEW_ROUTES),
          },
          // Monatsabschluss-Gate (F7-1): moved from /settings/month-close (PO
          // review 2026-09-16 — operational view, not admin config). The server
          // enforces :read (status) / :update (close, reopen); the route mirrors both.
          {
            path: 'month-close',
            canActivate: [requireAnyPermission('timetracking:month-close:read', 'timetracking:month-close:update')],
            loadComponent: () => import('./routes/month-close.component').then(m => m.MonthClosePageComponent),
            title: 'Month Close - SmartCapacity',
            data: { breadcrumb: { label: 'navigation.monthClose', icon: 'lock' } },
          },
        ],
      },
      // Projects domain (spec project-time-tracking, Story 8): self-service
      // capture at /projects (+ /projects/my-entries), master-data admin at
      // /projects/admin (+ /projects/admin/activity-types) — mirrors the
      // timetracking split of self-service vs. admin pages.
      {
        path: 'projects',
        canActivate: [requireAnyPermission('projects:my-time:read', 'projects:read'), featureGuard],
        data: {
          feature: 'projects',
          breadcrumb: { label: 'navigation.sectionProjects', icon: 'folder' },
        },
        children: [
          {
            path: 'admin',
            // `projects:project-time:approve`-only holders (spec: faktura.md,
            // Story 3 — e.g. a `responsible` employee without projects:read)
            // may only ever reach `:id/approve` productively: the other
            // admin screens (list, cost-rates, edit) call endpoints that
            // still require projects:read/update and 403 for them server-side.
            canActivate: [requireAnyPermission('projects:read', 'projects:project-time:approve')],
            // The buildable lib must not import @zag/auth/ui — its components
            // inject FIELD_PERMISSION_CHECKER, bound to the app PermissionService here.
            providers: [{ provide: FIELD_PERMISSION_CHECKER, useExisting: PermissionService }],
            data: { breadcrumb: { label: 'navigation.projectsAdmin', icon: 'folder' } },
            loadChildren: () => import('@zag/projects/ui').then(m => m.PROJECTS_ADMIN_ROUTES),
          },
          {
            path: '',
            canActivate: [requirePermission('projects:my-time:read')],
            data: { breadcrumb: { label: 'navigation.projectTime', icon: 'work_history' } },
            loadChildren: () => import('@zag/projects/ui').then(m => m.PROJECT_TIME_ROUTES),
          },
        ],
      },
      {
        path: 'settings',
        canActivate: [
          requireAnyPermission(
            'settings:update',
            'settings:read',
            'settings:auth:read',
            'settings:smtp:read',
            'settings:ai:read',
            'settings:directory:read',
            'notifications:admin',
            'users:profile:read',
            'webhooks:read',
            'files:read',
            'documents:configure'
          ),
        ],
        data: {
          breadcrumb: { label: 'navigation.settings', icon: 'settings' },
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./routes/settings.component').then(m => m.SettingsComponent),
            title: 'Settings - SmartCapacity',
          },
          {
            path: '',
            loadChildren: () => import('./routes/settings.routes').then(m => m.SETTINGS_ROUTES),
          },
        ],
      },
      {
        path: 'skills-catalog',
        canActivate: [requirePermission('skills:read')],
        data: {
          breadcrumb: { label: 'navigation.skillsCatalog', icon: 'school' },
        },
        loadChildren: () => import('./routes/skills-catalog.routes').then(m => m.SKILLS_CATALOG_ROUTES),
      },
      {
        path: 'teams',
        canActivate: [requirePermission('teams:read')],
        data: {
          breadcrumb: { label: 'navigation.teams', icon: 'groups' },
        },
        loadChildren: () => import('./routes/teams.routes').then(m => m.TEAMS_ROUTES),
      },
      {
        path: 'locations',
        canActivate: [requirePermission('locations:read')],
        data: {
          breadcrumb: { label: 'navigation.locations', icon: 'location_on' },
        },
        loadChildren: () => import('./routes/locations.routes').then(m => m.LOCATIONS_ROUTES),
      },
      {
        path: 'notifications',
        canActivate: [requirePermission('notifications:read')],
        data: {
          breadcrumb: { label: 'navigation.notifications', icon: 'notifications' },
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./routes/notifications/notification-list.component').then(m => m.NotificationListComponent),
            title: 'Notifications - SmartCapacity',
          },
          {
            path: 'preferences',
            redirectTo: '/profile',
          },
          {
            path: ':id',
            loadComponent: () => import('./routes/notifications/notification-detail.component').then(m => m.NotificationDetailComponent),
            title: 'Notification - SmartCapacity',
            data: {
              breadcrumb: {
                label: ':id',
                resolver: 'notification',
                siblingsLoader: 'notifications',
              },
            },
          },
        ],
      },
      {
        path: 'calendars-list',
        canActivate: [requirePermission('calendars:read')],
        data: {
          breadcrumb: { label: 'navigation.calendarsList', icon: 'calendar_month' },
        },
        loadChildren: () => import('./routes/calendars-list.routes').then(m => m.CALENDARS_LIST_ROUTES),
      },
      {
        path: 'calendars',
        canActivate: [requirePermission('calendars:read')],
        title: 'Calendar - SmartCapacity',
        data: {
          breadcrumb: {
            label: 'navigation.calendar',
            icon: 'calendar_today',
          },
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('./routes/calendar-edit.component').then(m => m.CalendarEditComponent),
          },
          {
            path: ':calendarId/slots/new',
            canActivate: [requirePermission('calendars:create')],
            loadComponent: () => import('./routes/calendar-slot-edit.component').then(m => m.CalendarSlotEditComponent),
            title: 'Create Calendar Slot - SmartCapacity',
            data: {
              breadcrumb: { label: 'calendars.appointment.createTitle' },
            },
          },
          {
            path: ':calendarId/slots/:slotId',
            canActivate: [requirePermission('calendars:read')],
            loadComponent: () => import('./routes/calendar-slot-edit.component').then(m => m.CalendarSlotEditComponent),
            title: 'Edit Calendar Slot - SmartCapacity',
            data: {
              breadcrumb: { label: 'calendars.appointment.editTitle' },
            },
          },
        ],
      },
    ],
  },
];
